import { FC } from 'react';
import { Paper, Group, Text, Stack, Badge, Anchor } from '@mantine/core';
import { Patient, Practitioner, ServiceRequest } from '@medplum/fhirtypes';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useDateDisplay } from '@hooks/useDateDisplay';
import { OrderFormData } from '../components/OrderForm';
import { getPatientOrPractitionerName } from '../utils/common-helpers';
import { getCodeableConceptDisplay } from '../utils/specimen-utils';
import {
  IconCalendarPlus,
  IconIdBadge2,
  IconStethoscope,
  IconTestPipe2Filled,
} from '@tabler/icons-react';

export interface OrderInfoBannerProps {
  serviceRequest: ServiceRequest;
  patient?: Patient;
  requester?: Practitioner;
  formData: OrderFormData;
}

const statusColorMap: Record<string, string> = {
  draft: 'gray',
  active: 'blue',
  'on-hold': 'yellow',
  revoked: 'red',
  completed: 'green',
  'entered-in-error': 'red',
  unknown: 'gray',
};

export const OrderInfoBanner: FC<OrderInfoBannerProps> = ({
  serviceRequest,
  patient,
  requester,
  formData,
}) => {
  const { t } = useTranslation('lims');
  const formatDateTime = useDateDisplay();

  const orderId = serviceRequest?.identifier?.[0]?.value ?? 'N/A';

  const status = serviceRequest.status;
  const statusColor = statusColorMap[status ?? ''] ?? 'gray';

  return (
    <Paper p="md">
      <Stack gap="xs">
        {/* Title row: exam name + status */}
        <Group justify="space-between">
          <Group gap="sm">
            <Text size="lg" fw={600}>
              {formData.testDisplay || formData.testCode || '—'}
            </Text>
            <Badge variant="light" size="lg">
              <Text
                component="span"
                size="sm"
                fw={500}
                c="dark"
                style={{ textTransform: 'none' }}
              >
                {t('orderDisplay.labels.patient')}:
              </Text>{' '}
              {patient?.id ? (
                <Anchor
                  size="sm"
                  component={Link}
                  to={`/Patient/${patient.id}`}
                  style={{ textTransform: 'none' }}
                >
                  {getPatientOrPractitionerName(patient)}
                </Anchor>
              ) : (
                <Text size="sm" style={{ textTransform: 'none' }}>
                  {getPatientOrPractitionerName(patient)}
                </Text>
              )}
            </Badge>
          </Group>
          <Badge color={statusColor} variant="light">
            {t(`orderDetails.status.${status}`, { defaultValue: status })}
          </Badge>
        </Group>

        {/* Details row */}
        <Group gap="lg" align="center">
          <Group gap="xs">
            <IconIdBadge2 size={16} color="var(--mantine-color-dimmed)" />
            <Text size="sm" c="dimmed">
              <Text component="span" size="sm" fw={500} c="dark">
                ID:
              </Text>{' '}
              {orderId}
            </Text>
          </Group>

          <Group gap="xs">
            <IconStethoscope size={16} color="var(--mantine-color-dimmed)" />
            <Text size="sm" c="dimmed">
              <Text component="span" size="sm" fw={500} c="dark">
                {t('orderDisplay.labels.requester')}:
              </Text>{' '}
              {requester?.id ? (
                <Anchor
                  size="sm"
                  component={Link}
                  to={`/Practitioner/${requester.id}`}
                >
                  {getPatientOrPractitionerName(requester)}
                </Anchor>
              ) : (
                <Text size="sm">{getPatientOrPractitionerName(requester)}</Text>
              )}
            </Text>
          </Group>

          <Group gap="xs">
            <IconTestPipe2Filled
              size={16}
              color="var(--mantine-color-dimmed)"
            />
            <Text size="sm" c="dimmed">
              <Text component="span" size="sm" fw={500} c="dark">
                {t('orderDisplay.labels.sampleType')}:
              </Text>{' '}
              {getCodeableConceptDisplay(formData.sampleType)}
            </Text>
          </Group>

          <Group gap="xs">
            <IconCalendarPlus size={16} color="var(--mantine-color-dimmed)" />
            <Text size="sm" c="dimmed">
              <Text component="span" size="sm" fw={500} c="dark">
                {t('orderDisplay.labels.creationDate')}:
              </Text>{' '}
              {formatDateTime(serviceRequest.authoredOn)}
            </Text>
          </Group>
        </Group>
      </Stack>
    </Paper>
  );
};
